import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { Component } from 'react';
import { errorTracker, performanceMonitor } from '@textile-erp/core';
import { FallbackUI } from './FallbackUI';
import { errorLogger } from './ErrorLogger';
export class GlobalErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.retryTimeout = null;
        this.retryError = () => {
            console.log('🔄 Retrying after error...');
            this.setState(prevState => ({
                hasError: false,
                error: null,
                errorInfo: null,
                retryCount: prevState.retryCount + 1,
            }));
            if (this.retryTimeout) {
                clearTimeout(this.retryTimeout);
                this.retryTimeout = null;
            }
        };
        this.resetErrorBoundary = () => {
            console.log('🔄 Manually resetting error boundary...');
            this.setState({
                hasError: false,
                error: null,
                errorInfo: null,
                retryCount: 0,
                lastErrorTime: 0,
            });
            if (this.retryTimeout) {
                clearTimeout(this.retryTimeout);
                this.retryTimeout = null;
            }
        };
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            retryCount: 0,
            lastErrorTime: 0,
        };
        // Initialize monitoring if enabled
        if (props.enablePerformanceMonitoring !== false) {
            performanceMonitor.mark('app_start');
        }
    }
    static getDerivedStateFromError(error) {
        return {
            hasError: true,
            error,
            lastErrorTime: Date.now(),
        };
    }
    componentDidCatch(error, errorInfo) {
        const { onError, enableErrorTracking = true, enableAutoReporting = true, maxRetries = 3, retryDelay = 2000 } = this.props;
        // Update state with error info
        this.setState({ errorInfo });
        // Log error with enhanced context
        const errorContext = {
            component: 'GlobalErrorBoundary',
            url: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: Date.now(),
            retryCount: this.state.retryCount,
            props: this.sanitizeProps(),
            performanceMetrics: this.getPerformanceSnapshot()
        };
        // Use error logger
        if (enableErrorTracking) {
            errorLogger.logError(error, errorInfo, 'error', errorContext);
            errorTracker.trackError(error, 'error', errorContext);
        }
        // Call custom error handler
        if (onError) {
            onError(error, errorInfo);
        }
        // Console logging in development
        if (process.env.NODE_ENV === 'development') {
            console.group('🚨 Global Error Boundary Caught Error');
            console.error('Error:', error);
            console.error('Error Info:', errorInfo);
            console.error('Context:', errorContext);
            console.groupEnd();
        }
        // Auto-reporting to external service
        if (enableAutoReporting) {
            this.reportErrorToService(error, errorInfo, errorContext);
        }
        // Auto-retry logic with exponential backoff
        if (this.state.retryCount < maxRetries) {
            const delay = retryDelay * Math.pow(2, this.state.retryCount);
            console.log(`⏱️ Auto-retry in ${delay}ms (attempt ${this.state.retryCount + 1}/${maxRetries})`);
            this.retryTimeout = setTimeout(() => {
                this.retryError();
            }, delay);
        }
    }
    sanitizeProps() {
        try {
            // Create a safe version of props without sensitive data
            return {
                childrenType: typeof this.props.children,
                hasCustomFallback: !!this.props.fallback,
                enableErrorTracking: this.props.enableErrorTracking,
                enablePerformanceMonitoring: this.props.enablePerformanceMonitoring,
            };
        }
        catch {
            return { sanitizationFailed: true };
        }
    }
    getPerformanceSnapshot() {
        try {
            const summary = performanceMonitor.getPerformanceSummary();
            return {
                pageLoadTime: summary.pageLoad,
                memoryUsage: summary.memoryUsage,
                errorOccurredAt: performance.now(),
            };
        }
        catch {
            return { performanceSnapshotFailed: true };
        }
    }
    async reportErrorToService(error, errorInfo, context) {
        try {
            // This would typically send to your error reporting service
            // For now, just log the intention
            console.log('📡 Reporting error to monitoring service:', {
                error: {
                    name: error.name,
                    message: error.message,
                    stack: error.stack?.slice(0, 1000), // Truncate stack trace
                },
                errorInfo: {
                    componentStack: errorInfo.componentStack?.slice(0, 1000),
                },
                context,
                timestamp: new Date().toISOString(),
            });
            // Example API call (replace with your actual endpoint):
            /*
            await fetch('/api/errors', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                error: {
                  name: error.name,
                  message: error.message,
                  stack: error.stack,
                },
                errorInfo,
                context,
                timestamp: new Date().toISOString(),
              }),
            });
            */
        }
        catch (reportError) {
            console.error('Failed to report error to service:', reportError);
        }
    }
    componentWillUnmount() {
        if (this.retryTimeout) {
            clearTimeout(this.retryTimeout);
        }
    }
    render() {
        const { hasError, error, errorInfo, retryCount } = this.state;
        const { children, fallback, maxRetries = 3 } = this.props;
        if (hasError && error) {
            // Use custom fallback if provided
            if (fallback) {
                const CustomFallback = fallback;
                return (_jsx(CustomFallback, { error: error, errorInfo: errorInfo || undefined, onReset: this.resetErrorBoundary }));
            }
            // Show different UI based on retry count
            if (retryCount >= maxRetries) {
                // Final fallback - critical error state
                return (_jsx(FallbackUI, { error: error, errorInfo: errorInfo || undefined, onReset: this.resetErrorBoundary, level: "page", showDetails: process.env.NODE_ENV === 'development', customMessage: `The application encountered a critical error after ${maxRetries} retry attempts. Please refresh the page or contact support.`, customActions: _jsxs("div", { className: "flex gap-2 mt-4", children: [_jsx("button", { onClick: () => window.location.reload(), className: "px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700", children: "Reload Application" }), _jsx("button", { onClick: () => {
                                    errorLogger.clearLogs();
                                    errorTracker.clearErrorData();
                                    window.location.reload();
                                }, className: "px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700", children: "Clear Data & Reload" })] }) }));
            }
            // Default error UI with retry information
            return (_jsx(FallbackUI, { error: error, errorInfo: errorInfo || undefined, onReset: this.resetErrorBoundary, level: "page", showDetails: process.env.NODE_ENV === 'development', customMessage: `Something went wrong. ${retryCount > 0 ? `Retry attempt ${retryCount}/${maxRetries}.` : 'The system will automatically retry.'}`, customActions: retryCount > 0 ? (_jsxs("div", { className: "text-sm text-gray-600 mt-2", children: ["Previous attempts: ", retryCount] })) : undefined }));
        }
        return children;
    }
}
// Hook for using global error boundary context
export function useGlobalErrorHandler() {
    const reportError = React.useCallback((error, context) => {
        // Report error through the monitoring system
        errorLogger.logError(error, undefined, 'error', context);
        errorTracker.trackError(error, 'error', context);
    }, []);
    const reportWarning = React.useCallback((message, context) => {
        const warning = new Error(message);
        warning.name = 'Warning';
        errorLogger.logError(warning, undefined, 'warning', context);
        errorTracker.trackError(warning, 'warning', context);
    }, []);
    const getErrorMetrics = React.useCallback(() => {
        return {
            logs: errorLogger.getLogs(),
            metrics: errorTracker.getErrorMetrics(),
            performance: performanceMonitor.getPerformanceSummary(),
        };
    }, []);
    return {
        reportError,
        reportWarning,
        getErrorMetrics,
    };
}
// HOC for components that want global error handling
export function withGlobalErrorHandling(Component, componentName) {
    const WrappedComponent = (props) => {
        const { reportError } = useGlobalErrorHandler();
        React.useEffect(() => {
            const handleError = (event) => {
                reportError(new Error(event.error || event.message), {
                    component: componentName || Component.displayName || Component.name,
                    source: 'window.onerror',
                    filename: event.filename,
                    lineno: event.lineno,
                    colno: event.colno,
                });
            };
            const handleUnhandledRejection = (event) => {
                reportError(new Error(`Unhandled Promise Rejection: ${event.reason}`), {
                    component: componentName || Component.displayName || Component.name,
                    source: 'unhandledrejection',
                    reason: event.reason,
                });
            };
            window.addEventListener('error', handleError);
            window.addEventListener('unhandledrejection', handleUnhandledRejection);
            return () => {
                window.removeEventListener('error', handleError);
                window.removeEventListener('unhandledrejection', handleUnhandledRejection);
            };
        }, [reportError]);
        return _jsx(Component, { ...props });
    };
    WrappedComponent.displayName = `withGlobalErrorHandling(${componentName || Component.displayName || Component.name})`;
    return WrappedComponent;
}
